import { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Heartbeat every 60s to keep login session duration accurate
const HEARTBEAT_MS = 60_000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const heartbeatRef = useRef(null);

  const startHeartbeat = () => {
    if (heartbeatRef.current) return;
    heartbeatRef.current = setInterval(() => {
      api.post("/auth/heartbeat").catch(() => {});
    }, HEARTBEAT_MS);
  };
  const stopHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("crm_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => { setUser(r.data); startHeartbeat(); })
      .catch(() => {
        localStorage.removeItem("crm_token");
        localStorage.removeItem("crm_user");
      })
      .finally(() => setLoading(false));
    return () => stopHeartbeat();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("crm_token", data.token);
    localStorage.setItem("crm_user", JSON.stringify(data.user));
    // Fresh login → reset ALL notice-dismissal flags so the important-notice
    // popup re-appears on every login (until admin turns it OFF).
    try {
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith("notice_dismissed_"))
        .forEach((k) => sessionStorage.removeItem(k));
    } catch {}
    setUser(data.user);
    startHeartbeat();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    stopHeartbeat();
    localStorage.removeItem("crm_token");
    localStorage.removeItem("crm_user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
